import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { deckService } from '../services/deckService';
import { DeckWithCreator } from '../types';

interface AdminUser {
  _id: string;
  name: string;
  email: string;
  roles: string[];
  subscriptionTier: 'free' | 'premium';
}

interface QuizItem {
  _id: string;
  title: string;
  description: string;
  type: string;
  questionsCount: number;
  tags: string[];
  attemptsCount: number;
  bestScore?: number;
  createdAt: string;
  userId: {
    _id: string;
    name: string;
    email: string;
  } | null;
  deckId?: string;
  deckName?: string;
}

interface MatchItem {
  _id: string;
  title: string;
  description: string;
  type: string;
  pairsCount: number;
  tags: string[];
  attemptsCount: number;
  bestTime?: number;
  createdAt: string;
  userId: {
    _id: string;
    name: string;
    email: string;
  } | null;
  deckId?: string;
  deckName?: string;
}

interface StudyItem {
  _id: string;
  title?: string;
  description?: string;
  subject?: string;
  category?: string;
  theme?: string;
  what?: string;
  where?: string;
  link?: string;
  status: string;
  priority: string;
  tags?: string[];
  start?: string;
  end?: string;
  progressPercentage: number;
  createdAt: string;
  updatedAt: string;
  userId: {
    _id: string;
    name: string;
    email: string;
  } | null;
}

interface PaginationInfo {
  page: number;
  limit: number;
  totalCount: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

type ContentType = 'decks' | 'quizzes' | 'matches' | 'studies';

const AdminContentManagement: React.FC = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [activeContentType, setActiveContentType] = useState<ContentType>('decks');
  const [searchTerm, setSearchTerm] = useState<string>('');
  
  // Decks state
  const [allDecks, setAllDecks] = useState<DeckWithCreator[]>([]);
  const [loadingDecks, setLoadingDecks] = useState(false);
  const [copyingDeck, setCopyingDeck] = useState<string | null>(null);
  const [decksPagination, setDecksPagination] = useState<PaginationInfo>({
    page: 1,
    limit: 9,
    totalCount: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false
  });
  
  // Quizzes state
  const [allQuizzes, setAllQuizzes] = useState<QuizItem[]>([]);
  const [loadingQuizzes, setLoadingQuizzes] = useState(false);
  const [copyingQuiz, setCopyingQuiz] = useState<string | null>(null);
  const [quizzesPagination, setQuizzesPagination] = useState<PaginationInfo>({
    page: 1,
    limit: 9,
    totalCount: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false
  });
  
  // Matches state
  const [allMatches, setAllMatches] = useState<MatchItem[]>([]);
  const [loadingMatches, setLoadingMatches] = useState(false);
  const [copyingMatch, setCopyingMatch] = useState<string | null>(null);
  const [matchesPagination, setMatchesPagination] = useState<PaginationInfo>({
    page: 1,
    limit: 9,
    totalCount: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false
  });
  
  // Studies state
  const [allStudies, setAllStudies] = useState<StudyItem[]>([]);
  const [loadingStudies, setLoadingStudies] = useState(false);
  const [studiesPagination, setStudiesPagination] = useState<PaginationInfo>({
    page: 1,
    limit: 9,
    totalCount: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false
  });
  
  // User selection modal
  const [showUserSelectionModal, setShowUserSelectionModal] = useState(false);
  const [selectedItemForCopy, setSelectedItemForCopy] = useState<{ id: string; name: string; type: ContentType } | null>(null);
  const [copyingToUser, setCopyingToUser] = useState<string | null>(null);
  const [userSearchTerm, setUserSearchTerm] = useState<string>('');
  const [allUsersForSelection, setAllUsersForSelection] = useState<AdminUser[]>([]);
  const [loadingAllUsersForSelection, setLoadingAllUsersForSelection] = useState(false);

  useEffect(() => {
    if (isAdmin) {
      // Load all content types on initial mount to get counts
      loadAllDecks();
      loadAllQuizzes();
      loadAllMatches();
      loadAllStudies();
    }
  }, [isAdmin]);

  useEffect(() => {
    if (isAdmin) {
      // Reset search when changing content type
      setSearchTerm('');
      // Reset to page 1 when changing content type
      if (activeContentType === 'decks') {
        setDecksPagination(prev => ({ ...prev, page: 1 }));
      } else if (activeContentType === 'quizzes') {
        setQuizzesPagination(prev => ({ ...prev, page: 1 }));
      } else if (activeContentType === 'matches') {
        setMatchesPagination(prev => ({ ...prev, page: 1 }));
      } else if (activeContentType === 'studies') {
        setStudiesPagination(prev => ({ ...prev, page: 1 }));
      }
      loadContent();
    }
  }, [isAdmin, activeContentType]);

  const loadContent = async () => {
    if (activeContentType === 'decks') {
      await loadAllDecks(searchTerm, decksPagination.page);
    } else if (activeContentType === 'quizzes') {
      await loadAllQuizzes(searchTerm, quizzesPagination.page);
    } else if (activeContentType === 'matches') {
      await loadAllMatches(searchTerm, matchesPagination.page);
    } else if (activeContentType === 'studies') {
      await loadAllStudies(searchTerm, studiesPagination.page);
    }
  };

  const loadAllDecks = async (search?: string, page: number = 1) => {
    try {
      setLoadingDecks(true);
      const response = await deckService.getAllDecks(search, page, decksPagination.limit);
      setAllDecks(response.decks);
      setDecksPagination(response.pagination);
    } catch (error) {
      console.error('Failed to load all decks:', error);
      setAllDecks([]);
    } finally {
      setLoadingDecks(false);
    }
  };

  const loadAllQuizzes = async (search?: string, page: number = 1) => {
    try {
      setLoadingQuizzes(true);
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      params.append('page', page.toString());
      params.append('limit', quizzesPagination.limit.toString());
      const response = await api.get(`/admin/quizzes?${params.toString()}`);
      setAllQuizzes(response.data.quizzes);
      setQuizzesPagination(response.data.pagination);
    } catch (error) {
      console.error('Failed to load all quizzes:', error);
      setAllQuizzes([]);
    } finally {
      setLoadingQuizzes(false);
    }
  };

  const loadAllMatches = async (search?: string, page: number = 1) => {
    try {
      setLoadingMatches(true);
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      params.append('page', page.toString());
      params.append('limit', matchesPagination.limit.toString());
      const response = await api.get(`/admin/matches?${params.toString()}`);
      setAllMatches(response.data.matches);
      setMatchesPagination(response.data.pagination);
    } catch (error) {
      console.error('Failed to load all matches:', error);
      setAllMatches([]);
    } finally {
      setLoadingMatches(false);
    }
  };

  const loadAllStudies = async (search?: string, page: number = 1) => {
    try {
      setLoadingStudies(true);
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      params.append('page', page.toString());
      params.append('limit', studiesPagination.limit.toString());
      const response = await api.get(`/admin/studies?${params.toString()}`);
      setAllStudies(response.data.studies);
      setStudiesPagination(response.data.pagination);
    } catch (error) {
      console.error('Failed to load all studies:', error);
      setAllStudies([]);
    } finally {
      setLoadingStudies(false);
    }
  };

  const loadAllUsersForSelection = async () => {
    try {
      const response = await api.get('/admin/users/selection');
      return response.data.users;
    } catch (error) {
      console.error('Failed to load all users for selection:', error);
      return [];
    }
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    // Reset to page 1 when searching
    if (activeContentType === 'decks') {
      setDecksPagination(prev => ({ ...prev, page: 1 }));
      await loadAllDecks(searchTerm, 1);
    } else if (activeContentType === 'quizzes') {
      setQuizzesPagination(prev => ({ ...prev, page: 1 }));
      await loadAllQuizzes(searchTerm, 1);
    } else if (activeContentType === 'matches') {
      setMatchesPagination(prev => ({ ...prev, page: 1 }));
      await loadAllMatches(searchTerm, 1);
    } else if (activeContentType === 'studies') {
      setStudiesPagination(prev => ({ ...prev, page: 1 }));
      await loadAllStudies(searchTerm, 1);
    }
  };

  const copyDeckToAdmin = async (deckId: string, deckName: string) => {
    if (!window.confirm(`Are you sure you want to copy "${deckName}" to the admin collection?`)) {
      return;
    }

    try {
      setCopyingDeck(deckId);
      await deckService.copyDeckToAdmin(deckId);
      alert(`Deck "${deckName}" has been successfully copied to your admin collection!`);
    } catch (error) {
      console.error('Failed to copy deck to admin:', error);
      alert('Failed to copy deck to admin. Please try again.');
    } finally {
      setCopyingDeck(null);
    }
  };

  const copyQuizToAdmin = async (quizId: string, quizTitle: string) => {
    if (!window.confirm(`Are you sure you want to copy "${quizTitle}" to the admin collection?`)) {
      return;
    }

    try {
      setCopyingQuiz(quizId);
      await api.post(`/admin/quizzes/${quizId}/copy-to-admin`);
      alert(`Quiz "${quizTitle}" has been successfully copied to your admin collection!`);
    } catch (error) {
      console.error('Failed to copy quiz to admin:', error);
      alert('Failed to copy quiz to admin. Please try again.');
    } finally {
      setCopyingQuiz(null);
    }
  };

  const copyMatchToAdmin = async (matchId: string, matchTitle: string) => {
    if (!window.confirm(`Are you sure you want to copy "${matchTitle}" to the admin collection?`)) {
      return;
    }

    try {
      setCopyingMatch(matchId);
      await api.post(`/admin/matches/${matchId}/copy-to-admin`);
      alert(`Match "${matchTitle}" has been successfully copied to your admin collection!`);
    } catch (error) {
      console.error('Failed to copy match to admin:', error);
      alert('Failed to copy match to admin. Please try again.');
    } finally {
      setCopyingMatch(null);
    }
  };

  const copyToUser = async (itemId: string, itemName: string, userId: string, userName: string, type: ContentType) => {
    if (!window.confirm(`Are you sure you want to copy "${itemName}" to user "${userName}"?`)) {
      return;
    }

    try {
      setCopyingToUser(itemId);
      
      if (type === 'decks') {
        await deckService.copyDeckToUser(itemId, userId);
      } else if (type === 'quizzes') {
        await api.post(`/admin/quizzes/${itemId}/copy-to-user`, { userId });
      } else if (type === 'matches') {
        await api.post(`/admin/matches/${itemId}/copy-to-user`, { userId });
      }
      
      setShowUserSelectionModal(false);
      setSelectedItemForCopy(null);
      alert(`${type === 'decks' ? 'Deck' : type === 'quizzes' ? 'Quiz' : 'Match'} "${itemName}" has been successfully copied to user "${userName}"!`);
    } catch (error) {
      console.error(`Failed to copy ${type} to user:`, error);
      alert(`Failed to copy ${type} to user. Please try again.`);
    } finally {
      setCopyingToUser(null);
    }
  };

  const openUserSelectionModal = async (itemId: string, itemName: string, type: ContentType) => {
    setSelectedItemForCopy({ id: itemId, name: itemName, type });
    setLoadingAllUsersForSelection(true);
    setShowUserSelectionModal(true);
    const allUsers = await loadAllUsersForSelection();
    setAllUsersForSelection(allUsers);
    setLoadingAllUsersForSelection(false);
  };

  if (!isAdmin) {
    return (
      <div className="text-center">
        <h1 className="text-2xl font-bold text-red-600 dark:text-red-400">Access Denied</h1>
        <p className="text-gray-600 dark:text-gray-400">You don't have permission to access this page.</p>
      </div>
    );
  }

  const isLoading = loadingDecks || loadingQuizzes || loadingMatches || loadingStudies;
  const currentItems = activeContentType === 'decks' ? allDecks 
    : activeContentType === 'quizzes' ? allQuizzes 
    : activeContentType === 'matches' ? allMatches 
    : allStudies;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Content Management</h1>
          <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">Manage decks, quizzes, matches, and studies across all users</p>
        </div>
        <button
          onClick={() => navigate('/admin')}
          className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
        >
          ← Back to Admin
        </button>
      </div>

      {/* Content Type Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveContentType('decks')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeContentType === 'decks'
                ? 'border-indigo-500 dark:border-indigo-400 text-indigo-600 dark:text-indigo-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
          >
            Decks ({decksPagination.totalCount})
          </button>
          <button
            onClick={() => setActiveContentType('quizzes')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeContentType === 'quizzes'
                ? 'border-indigo-500 dark:border-indigo-400 text-indigo-600 dark:text-indigo-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
          >
            Quizzes ({quizzesPagination.totalCount})
          </button>
          <button
            onClick={() => setActiveContentType('matches')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeContentType === 'matches'
                ? 'border-indigo-500 dark:border-indigo-400 text-indigo-600 dark:text-indigo-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
          >
            Matches ({matchesPagination.totalCount})
          </button>
          <button
            onClick={() => setActiveContentType('studies')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeContentType === 'studies'
                ? 'border-indigo-500 dark:border-indigo-400 text-indigo-600 dark:text-indigo-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
          >
            Studies ({studiesPagination.totalCount})
          </button>
        </nav>
      </div>

      {/* Search Bar */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="px-4 py-5 sm:p-6">
          <form onSubmit={handleSearch} className="flex items-center gap-2">
            <div className="relative flex-1">
              <input
                type="text"
                placeholder={`Search ${activeContentType}...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white rounded-lg transition-colors"
            >
              {isLoading ? 'Searching...' : 'Search'}
            </button>
            {searchTerm && (
              <button
                type="button"
                onClick={() => {
                  setSearchTerm('');
                  // Reset to page 1 when clearing search
                  if (activeContentType === 'decks') {
                    setDecksPagination(prev => ({ ...prev, page: 1 }));
                    loadAllDecks('', 1);
                  } else if (activeContentType === 'quizzes') {
                    setQuizzesPagination(prev => ({ ...prev, page: 1 }));
                    loadAllQuizzes('', 1);
                  } else if (activeContentType === 'matches') {
                    setMatchesPagination(prev => ({ ...prev, page: 1 }));
                    loadAllMatches('', 1);
                  }
                }}
                className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
              >
                Clear
              </button>
            )}
          </form>
        </div>
      </div>

      {/* Content List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 dark:border-indigo-400"></div>
        </div>
      ) : currentItems.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-gray-500 dark:text-gray-400">
            {searchTerm ? `No ${activeContentType} found matching your search.` : `No ${activeContentType} found.`}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {activeContentType === 'decks' && (allDecks as DeckWithCreator[]).map((deck) => (
            <div key={deck._id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-900 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-2">
                <h5 className="font-medium text-gray-900 dark:text-white">{deck.name}</h5>
                <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                  <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  {deck.userId ? deck.userId.name : 'Anonymous'}
                </div>
              </div>
              
              {deck.description && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">{deck.description}</p>
              )}
              
              <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1 mb-3">
                <div className="flex justify-between">
                  <span>Cards:</span>
                  <span>{deck.cardCount}</span>
                </div>
                <div className="flex justify-between">
                  <span>Studies:</span>
                  <span>{deck.studyCount}</span>
                </div>
                <div className="flex justify-between">
                  <span>Created:</span>
                  <span>{new Date(deck.createdAt).toLocaleDateString()}</span>
                </div>
                <div className="flex justify-between">
                  <span>Creator:</span>
                  <span>{deck.userId ? deck.userId.email : 'anonymous@unknown.com'}</span>
                </div>
              </div>
              
              {deck.tags && deck.tags.length > 0 && (
                <div className="mb-3 flex flex-wrap gap-1">
                  {deck.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-block px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
              
              <div className="pt-3 border-t border-gray-200 dark:border-gray-600 space-y-2">
                <button
                  onClick={() => copyDeckToAdmin(deck._id, deck.name)}
                  disabled={copyingDeck === deck._id}
                  className="w-full px-3 py-2 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 rounded-md transition-colors flex items-center justify-center gap-2"
                >
                  {copyingDeck === deck._id ? (
                    <>
                      <div className="animate-spin rounded-full h-3 w-3 border-b border-white"></div>
                      Copying...
                    </>
                  ) : (
                    <>
                      <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      Copy to Admin
                    </>
                  )}
                </button>
                <button
                  onClick={() => openUserSelectionModal(deck._id, deck.name, 'decks')}
                  disabled={copyingToUser === deck._id}
                  className="w-full px-3 py-2 text-xs font-medium text-white bg-green-600 hover:bg-green-700 disabled:bg-green-400 rounded-md transition-colors flex items-center justify-center gap-2"
                >
                  {copyingToUser === deck._id ? (
                    <>
                      <div className="animate-spin rounded-full h-3 w-3 border-b border-white"></div>
                      Copying...
                    </>
                  ) : (
                    <>
                      <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      Copy to User
                    </>
                  )}
                </button>
              </div>
            </div>
          ))}

          {activeContentType === 'quizzes' && allQuizzes.map((quiz) => (
            <div key={quiz._id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-900 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-2">
                <h5 className="font-medium text-gray-900 dark:text-white">{quiz.title}</h5>
                <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                  <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  {quiz.userId ? quiz.userId.name : 'Anonymous'}
                </div>
              </div>
              
              {quiz.description && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">{quiz.description}</p>
              )}
              
              <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1 mb-3">
                <div className="flex justify-between">
                  <span>Questions:</span>
                  <span>{quiz.questionsCount}</span>
                </div>
                <div className="flex justify-between">
                  <span>Attempts:</span>
                  <span>{quiz.attemptsCount}</span>
                </div>
                <div className="flex justify-between">
                  <span>Best Score:</span>
                  <span>{quiz.bestScore !== undefined ? `${quiz.bestScore}%` : 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span>Created:</span>
                  <span>{new Date(quiz.createdAt).toLocaleDateString()}</span>
                </div>
                <div className="flex justify-between">
                  <span>Creator:</span>
                  <span>{quiz.userId ? quiz.userId.email : 'anonymous@unknown.com'}</span>
                </div>
              </div>
              
              {quiz.tags && quiz.tags.length > 0 && (
                <div className="mb-3 flex flex-wrap gap-1">
                  {quiz.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-block px-2 py-1 text-xs bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 rounded"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
              
              <div className="pt-3 border-t border-gray-200 dark:border-gray-600 space-y-2">
                <button
                  onClick={() => copyQuizToAdmin(quiz._id, quiz.title)}
                  disabled={copyingQuiz === quiz._id}
                  className="w-full px-3 py-2 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 rounded-md transition-colors flex items-center justify-center gap-2"
                >
                  {copyingQuiz === quiz._id ? (
                    <>
                      <div className="animate-spin rounded-full h-3 w-3 border-b border-white"></div>
                      Copying...
                    </>
                  ) : (
                    <>
                      <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      Copy to Admin
                    </>
                  )}
                </button>
                <button
                  onClick={() => openUserSelectionModal(quiz._id, quiz.title, 'quizzes')}
                  disabled={copyingToUser === quiz._id}
                  className="w-full px-3 py-2 text-xs font-medium text-white bg-green-600 hover:bg-green-700 disabled:bg-green-400 rounded-md transition-colors flex items-center justify-center gap-2"
                >
                  {copyingToUser === quiz._id ? (
                    <>
                      <div className="animate-spin rounded-full h-3 w-3 border-b border-white"></div>
                      Copying...
                    </>
                  ) : (
                    <>
                      <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      Copy to User
                    </>
                  )}
                </button>
              </div>
            </div>
          ))}

          {activeContentType === 'matches' && allMatches.map((match) => (
            <div key={match._id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-900 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-2">
                <h5 className="font-medium text-gray-900 dark:text-white">{match.title}</h5>
                <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                  <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  {match.userId ? match.userId.name : 'Anonymous'}
                </div>
              </div>
              
              {match.description && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">{match.description}</p>
              )}
              
              <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1 mb-3">
                <div className="flex justify-between">
                  <span>Pairs:</span>
                  <span>{match.pairsCount}</span>
                </div>
                <div className="flex justify-between">
                  <span>Attempts:</span>
                  <span>{match.attemptsCount}</span>
                </div>
                <div className="flex justify-between">
                  <span>Best Time:</span>
                  <span>{match.bestTime !== undefined ? `${match.bestTime}s` : 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span>Created:</span>
                  <span>{new Date(match.createdAt).toLocaleDateString()}</span>
                </div>
                <div className="flex justify-between">
                  <span>Creator:</span>
                  <span>{match.userId ? match.userId.email : 'anonymous@unknown.com'}</span>
                </div>
              </div>
              
              {match.tags && match.tags.length > 0 && (
                <div className="mb-3 flex flex-wrap gap-1">
                  {match.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-block px-2 py-1 text-xs bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-300 rounded"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
              
              <div className="pt-3 border-t border-gray-200 dark:border-gray-600 space-y-2">
                <button
                  onClick={() => copyMatchToAdmin(match._id, match.title)}
                  disabled={copyingMatch === match._id}
                  className="w-full px-3 py-2 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 rounded-md transition-colors flex items-center justify-center gap-2"
                >
                  {copyingMatch === match._id ? (
                    <>
                      <div className="animate-spin rounded-full h-3 w-3 border-b border-white"></div>
                      Copying...
                    </>
                  ) : (
                    <>
                      <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      Copy to Admin
                    </>
                  )}
                </button>
                <button
                  onClick={() => openUserSelectionModal(match._id, match.title, 'matches')}
                  disabled={copyingToUser === match._id}
                  className="w-full px-3 py-2 text-xs font-medium text-white bg-green-600 hover:bg-green-700 disabled:bg-green-400 rounded-md transition-colors flex items-center justify-center gap-2"
                >
                  {copyingToUser === match._id ? (
                    <>
                      <div className="animate-spin rounded-full h-3 w-3 border-b border-white"></div>
                      Copying...
                    </>
                  ) : (
                    <>
                      <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      Copy to User
                    </>
                  )}
                </button>
              </div>
            </div>
          ))}

          {activeContentType === 'studies' && allStudies.map((study) => (
            <div key={study._id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-900 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-2">
                <h5 className="font-medium text-gray-900 dark:text-white">
                  {study.title || 'Untitled Study'}
                </h5>
                <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                  <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  {study.userId ? study.userId.name : 'Anonymous'}
                </div>
              </div>
              
              {study.description && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">{study.description}</p>
              )}
              
              <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1 mb-3">
                {study.subject && (
                  <div className="flex justify-between">
                    <span>Subject:</span>
                    <span>{study.subject}</span>
                  </div>
                )}
                {study.category && (
                  <div className="flex justify-between">
                    <span>Category:</span>
                    <span>{study.category}</span>
                  </div>
                )}
                {study.theme && (
                  <div className="flex justify-between">
                    <span>Theme:</span>
                    <span>{study.theme}</span>
                  </div>
                )}
                {study.what && (
                  <div className="flex justify-between">
                    <span>Type:</span>
                    <span>{study.what}</span>
                  </div>
                )}
                {study.where && (
                  <div className="flex justify-between">
                    <span>Platform:</span>
                    <span>{study.where}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span>Status:</span>
                  <span className={`inline-block px-2 py-0.5 text-xs rounded ${
                    study.status === 'completed' 
                      ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300'
                      : study.status === 'in_progress'
                      ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300'
                      : study.status === 'paused'
                      ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-300'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                  }`}>
                    {study.status.replace('_', ' ')}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Priority:</span>
                  <span className={`inline-block px-2 py-0.5 text-xs rounded ${
                    study.priority === 'urgent'
                      ? 'bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300'
                      : study.priority === 'high'
                      ? 'bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-300'
                      : study.priority === 'medium'
                      ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-300'
                      : 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300'
                  }`}>
                    {study.priority}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Progress:</span>
                  <span>{study.progressPercentage}%</span>
                </div>
                {study.start && (
                  <div className="flex justify-between">
                    <span>Start Date:</span>
                    <span>{new Date(study.start).toLocaleDateString()}</span>
                  </div>
                )}
                {study.end && (
                  <div className="flex justify-between">
                    <span>End Date:</span>
                    <span>{new Date(study.end).toLocaleDateString()}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span>Created:</span>
                  <span>{new Date(study.createdAt).toLocaleDateString()}</span>
                </div>
                <div className="flex justify-between">
                  <span>User:</span>
                  <span>{study.userId ? study.userId.email : 'anonymous@unknown.com'}</span>
                </div>
                {study.link && (
                  <div className="flex items-center justify-between">
                    <span>Link:</span>
                    <a 
                      href={study.link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-indigo-600 dark:text-indigo-400 hover:underline flex items-center"
                    >
                      <svg className="h-3 w-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                      Open
                    </a>
                  </div>
                )}
              </div>
              
              {study.tags && study.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-3">
                  {study.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-block px-2 py-1 text-xs bg-teal-100 dark:bg-teal-900 text-teal-700 dark:text-teal-300 rounded"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Pagination Controls */}
      {!isLoading && currentItems.length > 0 && (() => {
        const currentPagination = activeContentType === 'decks' 
          ? decksPagination 
          : activeContentType === 'quizzes' 
          ? quizzesPagination 
          : activeContentType === 'matches'
          ? matchesPagination
          : studiesPagination;

        const handlePageChange = (newPage: number) => {
          if (activeContentType === 'decks') {
            setDecksPagination(prev => ({ ...prev, page: newPage }));
            loadAllDecks(searchTerm, newPage);
          } else if (activeContentType === 'quizzes') {
            setQuizzesPagination(prev => ({ ...prev, page: newPage }));
            loadAllQuizzes(searchTerm, newPage);
          } else if (activeContentType === 'matches') {
            setMatchesPagination(prev => ({ ...prev, page: newPage }));
            loadAllMatches(searchTerm, newPage);
          } else if (activeContentType === 'studies') {
            setStudiesPagination(prev => ({ ...prev, page: newPage }));
            loadAllStudies(searchTerm, newPage);
          }
        };

        return (
          <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3 sm:px-6 rounded-b-lg">
            <div className="flex flex-1 justify-between sm:hidden">
              <button
                onClick={() => handlePageChange(currentPagination.page - 1)}
                disabled={!currentPagination.hasPrev}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => handlePageChange(currentPagination.page + 1)}
                disabled={!currentPagination.hasNext}
                className="relative ml-3 inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  Showing{' '}
                  <span className="font-medium">
                    {((currentPagination.page - 1) * currentPagination.limit) + 1}
                  </span>
                  {' '}to{' '}
                  <span className="font-medium">
                    {Math.min(currentPagination.page * currentPagination.limit, currentPagination.totalCount)}
                  </span>
                  {' '}of{' '}
                  <span className="font-medium">{currentPagination.totalCount}</span>
                  {' '}results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => handlePageChange(currentPagination.page - 1)}
                    disabled={!currentPagination.hasPrev}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Previous</span>
                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                  
                  {/* Page numbers */}
                  {(() => {
                    const pages = [];
                    const maxPagesToShow = 5;
                    let startPage = Math.max(1, currentPagination.page - Math.floor(maxPagesToShow / 2));
                    let endPage = Math.min(currentPagination.totalPages, startPage + maxPagesToShow - 1);
                    
                    if (endPage - startPage < maxPagesToShow - 1) {
                      startPage = Math.max(1, endPage - maxPagesToShow + 1);
                    }

                    for (let i = startPage; i <= endPage; i++) {
                      pages.push(
                        <button
                          key={i}
                          onClick={() => handlePageChange(i)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            i === currentPagination.page
                              ? 'z-10 bg-indigo-50 dark:bg-indigo-900 border-indigo-500 dark:border-indigo-400 text-indigo-600 dark:text-indigo-300'
                              : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600'
                          }`}
                        >
                          {i}
                        </button>
                      );
                    }
                    return pages;
                  })()}
                  
                  <button
                    onClick={() => handlePageChange(currentPagination.page + 1)}
                    disabled={!currentPagination.hasNext}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Next</span>
                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                </nav>
              </div>
            </div>
          </div>
        );
      })()}

      {/* User Selection Modal */}
      {showUserSelectionModal && selectedItemForCopy && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 dark:bg-gray-900 dark:bg-opacity-75 overflow-y-auto h-full w-full z-50 backdrop-blur-sm">
          <div className="relative top-20 mx-auto p-5 border border-gray-200 dark:border-gray-700 w-full max-w-md shadow-lg dark:shadow-2xl dark:shadow-black/50 rounded-md bg-white dark:bg-gray-800">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Select User to Copy {selectedItemForCopy.type === 'decks' ? 'Deck' : selectedItemForCopy.type === 'quizzes' ? 'Quiz' : 'Match'}
                </h3>
                <button
                  onClick={() => {
                    setShowUserSelectionModal(false);
                    setSelectedItemForCopy(null);
                    setUserSearchTerm('');
                    setAllUsersForSelection([]);
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                >
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="mb-4">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Copying: <strong>"{selectedItemForCopy.name}"</strong>
                </p>
              </div>

              {/* Search Input */}
              <div className="mb-4">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    value={userSearchTerm}
                    onChange={(e) => setUserSearchTerm(e.target.value)}
                    placeholder="Search users by name or email..."
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-indigo-500 dark:focus:border-indigo-400 text-sm"
                  />
                </div>
              </div>

              <div className="max-h-60 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-md">
                {(() => {
                  if (loadingAllUsersForSelection) {
                    return (
                      <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600 mx-auto mb-2"></div>
                        Loading users...
                      </div>
                    );
                  }

                  const filteredUsers = allUsersForSelection.filter(user => 
                    user.name.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
                    user.email.toLowerCase().includes(userSearchTerm.toLowerCase())
                  );

                  if (filteredUsers.length === 0) {
                    return (
                      <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                        {userSearchTerm ? 'No users found matching your search' : 'No users available'}
                      </div>
                    );
                  }

                  return (
                    <div className="divide-y divide-gray-200 dark:divide-gray-600">
                      {filteredUsers.map((user) => (
                        <button
                          key={user._id}
                          onClick={() => copyToUser(selectedItemForCopy.id, selectedItemForCopy.name, user._id, user.name, selectedItemForCopy.type)}
                          disabled={copyingToUser === selectedItemForCopy.id}
                          className="w-full p-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="font-medium text-gray-900 dark:text-white">{user.name}</div>
                              <div className="text-sm text-gray-500 dark:text-gray-400">{user.email}</div>
                              <div className="flex items-center mt-1">
                                {user.roles.map((role) => (
                                  <span
                                    key={role}
                                    className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium mr-1 ${
                                      role === 'admin' 
                                        ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300'
                                        : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                                    }`}
                                  >
                                    {role}
                                  </span>
                                ))}
                              </div>
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                user.subscriptionTier === 'premium' 
                                  ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300' 
                                  : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                              }`}>
                                {user.subscriptionTier}
                              </span>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  );
                })()}
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  onClick={() => {
                    setShowUserSelectionModal(false);
                    setSelectedItemForCopy(null);
                    setUserSearchTerm('');
                    setAllUsersForSelection([]);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminContentManagement;
